/* ============================================================================
   CUSTOM: Rausch-Ergänzungen zum Theme-JS.

   Bewusst eine eigene Datei (und nicht theme.js), damit ein Theme-Update sie
   nicht überschreibt. Wird in layout/theme.liquid nach theme.js geladen.
   ========================================================================== */

/* ---------------------------------------------------------------------------
   Mega-Menü-Bild-Slider: Autoplay pausiert beim Hovern und bei Tastatur-Fokus.

   Das Theme kennt nur Autoplay ohne Pause (EffectCarousel + Player in theme.js).
   Der Player entsteht erst in connectedCallback, deshalb wird er lazy im Handler
   geholt statt beim Binden — so ist die Reihenfolge von Custom-Element-Upgrade
   und diesem Script egal.

   player.pause() merkt sich die Restzeit, player.resume() läuft damit weiter
   (kein Neustart des Intervalls).

   Maus: nur echte Maus-Pointer lösen aus; auf Touch käme sonst kein pointerleave
   und der Slider bliebe nach einem Tap stehen.

   Tastatur: focusin/focusout am Wrapper (Slider + Pfeil-Buttons) erfüllt WCAG
   2.2.2 — wer sich per Tab durch den Flyout bewegt, kann den Wechsel anhalten,
   auch ohne Maus. focusin/focusout blubbern, mouseenter/focus nicht.
   --------------------------------------------------------------------------- */
function bindMegaMenuSliderPause(root) {
  const scope = root && root.querySelectorAll ? root : document;

  scope.querySelectorAll('mega-menu-promo-carousel[autoplay]').forEach((carousel) => {
    if (carousel.dataset.pauseBound) {
      return;
    }

    carousel.dataset.pauseBound = 'true';

    const pause = () => carousel.player?.pause();
    const resume = () => carousel.player?.resume();

    carousel.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'mouse') {
        pause();
      }
    });

    carousel.addEventListener('pointerleave', (event) => {
      if (event.pointerType === 'mouse') {
        resume();
      }
    });

    // Tastatur: der Wrapper umfasst Slider UND Pfeil-Buttons, damit der Wechsel
    // auch beim Sprung von der letzten Kachel auf die Buttons pausiert bleibt.
    const focusScope = carousel.closest('.navigation-promo__wrapper') || carousel;

    focusScope.addEventListener('focusin', pause);
    focusScope.addEventListener('focusout', (event) => {
      if (!focusScope.contains(event.relatedTarget)) {
        resume();
      }
    });
  });
}

bindMegaMenuSliderPause(document);

// Im Theme-Editor wird der Header neu gerendert — danach erneut binden.
document.addEventListener('shopify:section:load', (event) => bindMegaMenuSliderPause(event.target));


/* ---------------------------------------------------------------------------
   Homepage-Slider-Tracking laden.

   Lag vorher als Inline-<script> in sections/slideshow.liquid. Hierher gezogen,
   damit die Vanilla-Section unberuehrt bleibt; dort steht nur noch das Attribut
   data-homepage-slider-tracking am <slideshow-carousel>.

   Das Attribut setzt die Section nur auf der Startseite, deshalb genuegt die
   Abfrage hier als Gate. homepage-slider-tracking.js bringt seinen eigenen
   Doppellade-Schutz mit und wartet selbst auf DOMContentLoaded.

   Die Asset-URL kommt aus import.meta.url: In einer statischen .js-Datei gibt es
   keinen asset_url-Filter, aber custom.js wird als type="module" geladen und liegt
   im selben assets/-Verzeichnis. new URL(...) ersetzt nur den Dateinamen und
   trifft damit immer den echten CDN-Pfad des aktiven Themes.
   --------------------------------------------------------------------------- */
function loadHomepageSliderTracking() {
  if (window.__rauschHomepageSliderTrackingLoader) return;
  if (!document.querySelector('slideshow-carousel[data-homepage-slider-tracking="true"]')) return;

  window.__rauschHomepageSliderTrackingLoader = true;

  const script = document.createElement('script');
  script.src = new URL('homepage-slider-tracking.js', import.meta.url).href;
  script.defer = true;
  document.head.appendChild(script);
}

loadHomepageSliderTracking();
document.addEventListener('shopify:section:load', loadHomepageSliderTracking);


/* ---------------------------------------------------------------------------
   Footer-Menüs mobil aufklappbar (unter 700px).

   sections/footer.liquid bleibt Vanilla: Die Überschrift <p class="bold"> eines
   Menü-Blocks bekommt hier per JS einen <button> mit aria-expanded, der die
   Liste darunter ein- und ausklappt. Ab 700px wird der Button wieder entfernt,
   dann stehen die Menüs wie gewohnt offen nebeneinander.

   Progressive Enhancement: Ohne JS bleiben alle Menüs offen, weil custom.css
   nur Blöcke mit .is-collapsible zuklappt. Menüs ohne sichtbare Überschrift
   (Setting "Menütitel zeigen" aus) werden nicht angefasst.
   --------------------------------------------------------------------------- */
const footerMenuMedia = window.matchMedia('(max-width: 699px)');

function enhanceFooterMenus(root) {
  const scope = root && root.querySelectorAll ? root : document;
  const blocks = [...scope.querySelectorAll('.footer__block--menu')];

  blocks.forEach((block, index) => {
    const heading = block.querySelector(':scope > p');
    const list = block.querySelector(':scope > ul');

    if (!heading || !list) {
      return;
    }

    if (footerMenuMedia.matches) {
      if (block.classList.contains('is-collapsible')) {
        return;
      }

      list.id ||= `footer-menu-${block.closest('.shopify-section')?.id || 'footer'}-${index}`;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'footer__menu-toggle';
      button.setAttribute('aria-expanded', 'false');
      button.setAttribute('aria-controls', list.id);
      button.append(...heading.childNodes);
      heading.append(button);

      button.addEventListener('click', () => {
        const isOpen = block.classList.toggle('is-open');
        button.setAttribute('aria-expanded', String(isOpen));
      });

      block.classList.add('is-collapsible');
    } else if (block.classList.contains('is-collapsible')) {
      const button = heading.querySelector(':scope > .footer__menu-toggle');

      if (button) {
        heading.append(...button.childNodes);
        button.remove();
      }

      block.classList.remove('is-collapsible', 'is-open');
    }
  });
}

enhanceFooterMenus(document);
footerMenuMedia.addEventListener('change', () => enhanceFooterMenus(document));

// Im Theme-Editor wird der Footer beim Bearbeiten neu gerendert.
document.addEventListener('shopify:section:load', (event) => enhanceFooterMenus(event.target));


/* ---------------------------------------------------------------------------------------------------------------
   Ursprung: Kurzprofil-Slider
   Die Section-Überschrift liegt außerhalb der Slides. Beim Wechsel übernimmt sie die Überschriftfarbe des
   gewählten Slides (--herkunft-heading-color), sonst wäre sie auf einer dunklen Herkunftsfläche unlesbar.
   --------------------------------------------------------------------------------------------------------------- */
function syncHerkunftSliderHeading(slide) {
  const root = slide && slide.closest('.herkunft-slider');

  if (!root) {
    return;
  }

  const color = getComputedStyle(slide).getPropertyValue('--herkunft-heading-color').trim();

  if (color) {
    root.style.setProperty('--herkunft-slider-heading-color', color);
  }
}

document.addEventListener('carousel:select', (event) => syncHerkunftSliderHeading(event.detail && event.detail.slide));

function initHerkunftSliders(scope) {
  scope.querySelectorAll('.herkunft-slider__slide.is-selected').forEach(syncHerkunftSliderHeading);
}

initHerkunftSliders(document);
document.addEventListener('shopify:section:load', (event) => initHerkunftSliders(event.target));
